import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import { useAuth } from '../../contexts/AuthContext'
import { ToggleSwitch } from '../../components/ToggleSwitch'

interface Product {
  id: string
  name: string
  sku?: string
  price: number
  is_available: boolean
}

interface OrderItem {
  name: string
  qty: number
  price: number
}

interface Order {
  id: string
  buyer_name: string
  subtotal: number
  created_at: string
  items: OrderItem[]
}

interface OrdersResponse {
  orders: Order[]
}

export function TrabajadorPanel() {
  const { user, logout } = useAuth()
  const [products, setProducts] = useState<Product[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const today = new Date().toDateString()
  const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === today)

  useEffect(() => {
    Promise.all([
      api.get<Product[]>('/api/store/products'),
      api.get<OrdersResponse | Order[]>('/api/store/orders?limit=10'),
    ])
      .then(([prods, ordersData]) => {
        setProducts(prods)
        const list = Array.isArray(ordersData)
          ? ordersData
          : ((ordersData as OrdersResponse).orders ?? [])
        setOrders(list)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  async function toggleAvailability(p: Product) {
    const newVal = !p.is_available
    setProducts(ps => ps.map(x => x.id === p.id ? { ...x, is_available: newVal } : x))
    try {
      await api.patch(`/api/store/products/${p.id}/availability`, { is_available: newVal })
    } catch {
      setProducts(ps => ps.map(x => x.id === p.id ? { ...x, is_available: p.is_available } : x))
    }
  }

  if (loading) {
    return (
      <div className="trabajador-panel">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{ height: 72, borderRadius: 16 }} />)}
        </div>
      </div>
    )
  }

  return (
    <div className="trabajador-panel animate-in">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, var(--ink) 0%, var(--ink-2) 100%)',
        color: '#fff', borderRadius: 'var(--r-xl)', padding: '24px',
        marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
      }}>
        <div>
          <p style={{ fontSize: '.8rem', color: 'var(--muted-2)', marginBottom: 4 }}>Panel de Trabajador</p>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 2 }}>{user?.fullName}</h1>
          <p style={{ fontSize: '.85rem', color: 'var(--muted-2)' }}>
            {todayOrders.length} pedido{todayOrders.length !== 1 ? 's' : ''} hoy
          </p>
        </div>
        <button type="button" className="btn btn-ghost btn-sm"
          style={{ color: '#fff', borderColor: 'rgba(255,255,255,.2)', background: 'rgba(255,255,255,.08)' }}
          onClick={logout}>
          Salir
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Products availability */}
      <section className="trabajador-section">
        <h2>Disponibilidad de productos</h2>
        <p className="admin-sub" style={{ marginBottom: 12 }}>Toca el switch para cambiar el estado</p>
        {products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <p>No hay productos</p>
          </div>
        ) : (
          <div className="availability-list">
            {products.map(p => (
              <div key={p.id} className="availability-row">
                <div className="availability-info">
                  <strong>{p.name}</strong>
                  {p.sku && <span style={{ fontSize: '.78rem', color: 'var(--muted-2)', fontFamily: 'monospace' }}>SKU: {p.sku}</span>}
                  <span style={{ fontSize: '.85rem', color: 'var(--muted)' }}>Q {Number(p.price).toFixed(2)}</span>
                </div>
                <ToggleSwitch
                  checked={p.is_available}
                  onChange={() => toggleAvailability(p)}
                  size="lg"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Today's orders */}
      <section className="trabajador-section">
        <h2>Pedidos de hoy</h2>
        {todayOrders.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px 20px' }}>
            <div className="empty-icon">📋</div>
            <p>Sin pedidos hoy</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {todayOrders.map(order => (
              <div key={order.id} className="order-card animate-in">
                <div className="order-card-header-bar today">
                  <strong>{order.buyer_name}</strong>
                  <strong>Q {Number(order.subtotal).toFixed(2)}</strong>
                </div>
                <div className="order-card-body">
                  {order.items?.map((item, i) => (
                    <div key={i} className="order-item-row">
                      <span>{item.qty}x {item.name}</span>
                      <span style={{ fontSize: '.85rem', color: 'var(--muted)' }}>Q {(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
